import { useEffect, useMemo, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import {
  RELATION_HAS_MEMBER,
  stringifyEntityRef,
  UserEntity,
} from '@backstage/catalog-model';
import { getLeadershipRefs } from './leadershipRefs';

/**
 * Shared data-fetch hook for cards that read all User members of a Group
 * entity. Encapsulates:
 *
 *  - Reading the entity's `RELATION_HAS_MEMBER` user-typed targets
 *  - Batching the resolution via `catalogApi.getEntitiesByRefs` with a
 *    caller-provided field projection (saves N+1 requests per card)
 *  - Kind-guarding the results to UserEntity (drops anything the catalog
 *    might return that isn't actually a User — defence against catalog
 *    inconsistency or ref-format drift)
 *  - State reset + cancellation flag so a late-arriving promise from a
 *    previous group can't overwrite current state, and so a fetch
 *    failure surfaces an `error` rather than a silently empty card.
 *
 * Both EntityTeamMembersCard and EntityTeamCompositionCard call this
 * with their own field lists.
 */
export interface UseTeamMemberEntitiesResult {
  /** Resolved User entities (kind-guarded). May be shorter than the
   * underlying memberRefs if some refs failed to resolve. */
  items: UserEntity[];
  loading: boolean;
  /** Set when the catalog fetch itself failed (network, auth, 5xx).
   * Distinct from an empty `items` result. */
  error?: Error;
}

export interface UseTeamMemberEntitiesOptions {
  /** Union in leadership refs (team/lead, team/pm, team/tech-lead,
   * team/architect) instead of excluding them, deduped by ref against
   * regular members. For aggregate rollups (e.g. Team Composition) where
   * leadership are real people who should be counted once. */
  includeLeadership?: boolean;
  /** When false, skip the fetch entirely and return an inert, non-loading
   * result. Lets a caller that already receives this data via props (e.g.
   * EntityTeamMembersCard when rendered by EntityTeamRosterSection) still
   * call this hook unconditionally — satisfying the rules of hooks — without
   * issuing a second, duplicate `getEntitiesByRefs` request. Defaults to
   * true so existing callers are unaffected. */
  enabled?: boolean;
}

export function useTeamMemberEntities(
  fields: string[],
  options: UseTeamMemberEntitiesOptions = {},
): UseTeamMemberEntitiesResult {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const { includeLeadership = false, enabled = true } = options;

  // Backstage's useEntity() returns a context-stable entity reference — when
  // the catalog hasn't refetched, `entity` is the same object across renders,
  // so [entity] is a safe dep without stringification. We read both
  // relations and annotations from it, so depend on the whole entity.
  const memberRefs = useMemo(() => {
    const leadershipRefs = getLeadershipRefs(entity);
    const hasMemberRefs = (entity.relations ?? [])
      .filter(
        r =>
          r.type === RELATION_HAS_MEMBER &&
          r.targetRef.toLowerCase().startsWith('user:'),
      )
      .map(r => r.targetRef);

    if (includeLeadership) {
      const seen = new Set<string>();
      const refs: string[] = [];
      for (const ref of [...hasMemberRefs, ...leadershipRefs]) {
        const key = ref.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          refs.push(ref);
        }
      }
      return refs;
    }

    // Deduplicate leadership holders — they are already shown in the
    // Leadership card. Mirrors the edit form's MembersPicker
    // `excludeRefs` filter so view and edit member counts match.
    return hasMemberRefs.filter(ref => !leadershipRefs.has(ref.toLowerCase()));
  }, [entity, includeLeadership]);

  // Stable dep for the fields array — join into a single string so callers
  // can pass an inline literal without triggering re-fetches every render.
  const fieldsKey = fields.join(',');

  const [items, setItems] = useState<UserEntity[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<Error | undefined>();

  // EntityPage keeps entity-scoped components mounted across catalog
  // navigation, so `useEntity()` can return a new entity in a render before
  // our effect below has re-fetched for it — leaving `items`/`loading` from
  // the previous entity. Reset them synchronously the moment we notice the
  // entity changed, so this render can't paint a stale total made of the new
  // entity's leadership count plus the old entity's members.
  const entityRef = stringifyEntityRef(entity);
  const [resolvedEntityRef, setResolvedEntityRef] = useState(entityRef);
  if (entityRef !== resolvedEntityRef) {
    setResolvedEntityRef(entityRef);
    setItems([]);
    setError(undefined);
    setLoading(enabled);
  }

  useEffect(() => {
    if (!enabled) {
      setItems([]);
      setError(undefined);
      setLoading(false);
      return undefined;
    }
    setItems([]);
    setError(undefined);
    if (memberRefs.length === 0) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    let cancelled = false;
    catalogApi
      .getEntitiesByRefs({ entityRefs: memberRefs, fields })
      .then(({ items: results }) => {
        if (cancelled) return;
        const users: UserEntity[] = [];
        for (const e of results) {
          if (e && e.kind === 'User') {
            users.push(e as UserEntity);
          }
        }
        setItems(users);
      })
      .catch(e => {
        if (cancelled) return;
        setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // fields is referenced via its joined string for stable identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogApi, memberRefs, fieldsKey, enabled]);

  return { items, loading, error };
}
